import React, { memo, useState } from "react";
import { Box, Typography, Paper, Button, Grid } from "@mui/material";

import { styled } from "@mui/system";
import CopyIconButton from "components/atoms/CopyIconButton";
import StepContainer from "../../components/StepContainer";
import { EditLandingPageSteps } from "../../AddEditLandingPage.types";
import { ReactComponent as Facebook } from "../../assets/facebook.svg";
import { ReactComponent as Tiktok } from "../../assets/tiktok.svg";
import { ReactComponent as Snapchat } from "../../assets/snapchat.svg";
import GoogleAdsIcon from "../../assets/googleads.png";

const StyledCard = styled(Paper)(({ theme }) => ({
    borderRadius: 10,
    backgroundColor: "#f9f9f9",
    padding: theme.spacing(2),
    position: "relative",
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    boxShadow: "none",
}));
const IconPosition = styled(Box)({
    position: "absolute",
    top: -12,
    left: 12,
});

const CopyPosition = styled(Box)({
    position: "absolute",
    top: 12,
    right: 12,
});

const trackingData = [
    {
        label: "Facebook Tracking Parameters",
        icon: <Facebook />,
        noURL: true,
        param: `ad_id={{ad.id}}&adset_id={{adset.id}}&campaign_id={{campaign.id}}&ad_name={{ad.name}}&adset_name={{adset.name}}&campaign_name={{campaign.name}}&placement={{placement}}&site_source_name={{site_source_name}}`,
    },
    {
        label: "TikTok Tracking Parameters",
        icon: <Tiktok />,
        param: `?ad_id=__CID__&adset_id=__AID__&campaign_id=__CAMPAIGN_ID__&ad_name=__CID_NAME__&adset_name=__AID_NAME__&campaign_name=__CAMPAIGN_NAME__&placement=__PLACEMENT__&site_source_name=tt ⁠`,
    },
    {
        label: "Snapchat Tracking Parameters",
        icon: <Snapchat />,
        param: `?ad_id={{ad.id}}&adset_id={{adSet.id}}&campaign_id={{campaign.id}}&ad_name={{creative.name}}&adset_name={{adSet.name}}&campaign_name={{campaign.name}}&site_source_name={{site_source_name}}`,
    },
    {
        label: "Google Ads Tracking Parameters",
        icon: (
            <img src={GoogleAdsIcon} alt="Google Ads" width={24} height={24} />
        ),
        noURL: true,
        param: `{lpurl}?campaign_id={campaignid}&ad_name={creative}&adset_name={keyword}&campaign_name={_campaign}`,
    },
];

export default memo(function OverviewStep({
    pageURL,
    setStep,
    setCompletedSteps,
}) {
    const baseUrl = pageURL;

    // Generate unique timestamp for cache bypass - regenerates on each mount
    const [previewTimestamp] = useState(() => Date.now());
    const previewUrl = `${pageURL}?test=1&_t=${previewTimestamp}`;

    return (
        <StepContainer
            noMarginTop
            renderButtons={() => (
                <Box
                    sx={{
                        width: "100%",
                        display: "flex",
                        justifyContent: "flex-end",
                    }}
                >
                    <Button
                        variant="contained"
                        color="primary"
                        sx={{
                            ml: "auto",
                            height: "fit-content",
                        }}
                        onClick={() => {
                            setStep(EditLandingPageSteps.GENERAL);
                            setCompletedSteps((prev) => [
                                ...prev,
                                EditLandingPageSteps.OVERVIEW,
                            ]);
                        }}
                    >
                        CONTINUE
                    </Button>
                </Box>
            )}
        >
            <Grid container spacing={4} height={"100%"} alignItems="center">
                <Grid item size={{ xs: 12, lg: 6 }} height="fit-content">
                    <Box
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            height: "fit-content",
                        }}
                    >
                        {/* Landing Page URL card */}
                        <StyledCard>
                            <CopyPosition>
                                <CopyIconButton value={baseUrl} />
                            </CopyPosition>
                            <Typography
                                variant="body2"
                                fontWeight={600}
                                sx={{
                                    pl: 5,
                                    pr: 5,
                                    position: "absolute",
                                    top: -12,
                                }}
                            >
                                Landing Page URL
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{
                                    whiteSpace: "pre-line",
                                    color: "#205B00",
                                    fontSize: 13,
                                    pt: 2,
                                    pl: 5,
                                    pr: 5,
                                    wordBreak: "break-word",
                                }}
                            >
                                {baseUrl}
                            </Typography>
                        </StyledCard>

                        {/* Tracking Parameter Cards */}
                        <Box>
                            {trackingData.map((item, idx) => {
                                const fullUrl = `${!item.noURL ? baseUrl : ""}${
                                    item.param
                                }`;
                                return (
                                    <StyledCard key={idx} className="mt-5">
                                        <IconPosition>{item.icon}</IconPosition>
                                        <CopyPosition>
                                            <CopyIconButton value={fullUrl} />
                                        </CopyPosition>
                                        <Typography
                                            variant="body2"
                                            fontWeight={600}
                                            sx={{
                                                pl: 5,
                                                pr: 5,
                                                position: "absolute",
                                                top: -12,
                                            }}
                                        >
                                            {item.label}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                whiteSpace: "pre-line",
                                                color: "#205B00",
                                                fontSize: 13,
                                                pt: 2,
                                                pl: 5,
                                                pr: 5,
                                                wordBreak: "break-word",
                                            }}
                                        >
                                            {fullUrl}
                                        </Typography>
                                    </StyledCard>
                                );
                            })}
                        </Box>
                    </Box>
                </Grid>

                {/* Right side: preview area */}
                <Grid
                    item
                    size={{ xs: 12, lg: 6 }}
                    sx={{
                        height: {
                            xs: "fit-content",
                            lg: "100%",
                        },
                    }}
                >
                    <Box
                        sx={{
                            height: "100%",
                            width: "100%",
                            border: "1px solid #E0E0E0",
                            borderRadius: 2,
                        }}
                    >
                        <iframe
                            src={previewUrl}
                            style={{
                                width: "100%",
                                height: "100%",
                                border: "none",
                                minHeight: {
                                    xs: 600,
                                    lg: "100%",
                                },
                            }}
                            title="Landing Page Preview"
                        />
                    </Box>
                </Grid>
            </Grid>
        </StepContainer>
    );
});
